// Cognito OIDC config (PRD-0006 D2/D9). Entirely env-driven: when the
// VITE_COGNITO_* vars are absent (local dev against uvicorn + AUTH_DEV_PRINCIPAL,
// or the GCP/IAP deployment where the proxy owns auth), auth is disabled and the
// SPA behaves exactly as before — no login, no token attachment.

export interface CognitoConfig {
  authority: string; // https://cognito-idp.{region}.amazonaws.com/{poolId}
  clientId: string;
  // Cognito hosted-UI domain (fleet-dev-{acct}.auth.{region}.amazoncognito.com).
  // Needed to build the /logout URL — the OIDC discovery doc doesn't advertise
  // an end_session_endpoint, so signout is a Cognito-specific redirect. Empty
  // when the deploy predates the var: logout then falls back to a local-only
  // token clear (see UserBadge).
  hostedDomain: string;
}

export function cognitoConfig(): CognitoConfig | null {
  const authority = import.meta.env.VITE_COGNITO_AUTHORITY as string | undefined;
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;
  if (!authority || !clientId) return null;
  const hostedDomain =
    (import.meta.env.VITE_COGNITO_HOSTED_DOMAIN as string | undefined) ?? '';
  return { authority, clientId, hostedDomain };
}

// Cognito's logout endpoint (not the standard OIDC end_session_endpoint): clears
// the Cognito session, then redirects back to `redirectTo` — which must be in the
// app client's allowed logout URLs (terraform-aws/auth.tf). Returns null when the
// hosted domain isn't configured (older deploy) so the caller can fall back.
export function cognitoLogoutUrl(
  cfg: CognitoConfig,
  redirectTo: string,
): string | null {
  if (!cfg.hostedDomain) return null;
  const params = new URLSearchParams({
    client_id: cfg.clientId,
    logout_uri: redirectTo,
  });
  return `https://${cfg.hostedDomain}/logout?${params.toString()}`;
}

// oidc-client-ts persists the signed-in user in sessionStorage under this key;
// apiFetch reads it outside React (hooks are plain modules).
export function oidcStorageKey(cfg: CognitoConfig): string {
  return `oidc.user:${cfg.authority}:${cfg.clientId}`;
}
